<template>
  <SettingsPageLayout>
    <div v-if="loaded" class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <WorkflowCard
        title="Job Refresh"
        description="When a job description hasn't been updated in at least 4 weeks, Humaniz can remind you to update it. We
              have found that job descriptions which are updated regularly typically result in 20% more candidates. This
              is due to platforms like Indeed viewing older jobs as stale."
        :tags="['email']"
        :toggle-value="team.useJobRefreshWorkflow"
        :show-settings-button="true"
        :show-toggle="true"
        @update:toggle-value="updateJobRefresh($event)"
        @click-settings-button="showJobRefreshSettings = true"
      />

      <WorkflowCard
        title="New Teammate"
        description="When a new Teammate is added to Humaniz, Humaniz sends them a message from your team with their unique
              referral link so they can refer agents to your team ensuring that everything is tracked in Humaniz."
        :tags="['email', 'sms']"
        :toggle-value="team.notifyNewTeammateReferral"
        :show-settings-button="true"
        :show-toggle="true"
        @update:toggle-value="updateNewTeammateReferral($event)"
        @click-settings-button="showNewTeammateSettings = true"
      />

      <NeedAWorkflowCard />
    </div>

    <NewTeammateSettings
      v-if="showNewTeammateSettings"
      @handle-cancel-settings="showNewTeammateSettings = false"
      @handle-save-settings="showNewTeammateSettings = false"
    />
    <JobRefreshSettings
      v-if="showJobRefreshSettings"
      @handle-cancel-settings="showJobRefreshSettings = false"
      @handle-save-settings="showJobRefreshSettings = false"
    />
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import NewTeammateSettings from './settings/NewTeammateSettings.vue';
import JobRefreshSettings from './settings/JobRefreshSettings.vue';
import WorkflowCard from './WorkflowCard.vue';
import NeedAWorkflowCard from './NeedAWorkflowCard.vue';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';

export default {
  components: {
    SettingsPageLayout,
    NewTeammateSettings,
    JobRefreshSettings,
    WorkflowCard,
    NeedAWorkflowCard,
  },
  setup() {
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { loadingSpinnerStore };
  },
  data() {
    return {
      loaded: false,
      team: {
        id: '',
        notifyNewTeammateReferral: false,
        useJobRefreshWorkflow: false,
      },
      showNewTeammateSettings: false,
      showJobRefreshSettings: false,
    };
  },
  async mounted() {
    await this.getTeam();
  },
  methods: {
    handleCancelSettings() {
      const self = this;
      self.showSettings = false;
    },
    handleSaveSettings() {
      const self = this;
      self.showSettings = false;
    },
    async getTeam() {
      try {
        this.loadingSpinnerStore.showLoadingSpinner();
        const response = await fetch('/api/team');
        const data = await response.json();

        this.loadingSpinnerStore.hideLoadingSpinner();
        this.team.id = data.record.id;
        this.team.notifyNewTeammateReferral = data.record.notify_new_teammate_referral;
        this.team.useJobRefreshWorkflow = data.record.use_job_refresh_workflow;
        this.loaded = true;
      } catch {
        this.loadingSpinnerStore.hideLoadingSpinner();
        toast.error('Error loading team', {
          action: { label: 'Dismiss' },
          description: 'An unexpected error occurred',
        });
      }
    },
    async updateNewTeammateReferral(e) {
      try {
        // Update the local state with the new toggle value first
        this.team.notifyNewTeammateReferral = e;

        this.loadingSpinnerStore.showLocalLoadingSpinner();

        // Only proceed with API call if we have a valid team ID
        if (!this.team.id) {
          this.loadingSpinnerStore.hideLocalLoadingSpinner();
          toast.error('Workflow update error', {
            action: { label: 'Dismiss' },
            description: 'Unable to update: Team ID is missing',
          });
          return;
        }

        const response = await fetch('/api/team', {
          method: 'PUT',
          body: JSON.stringify({
            notify_new_teammate_referral: e,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });

        const data = await response.json();
        this.loadingSpinnerStore.hideLocalLoadingSpinner();

        if (data.status !== 'error') {
          toast.success('Workflow updated', {
            action: { label: 'Dismiss' },
            description: 'Your workflow was successfully updated. Use the button below to dismiss this alert.',
          });
        } else {
          toast.error('Workflow update error', {
            action: { label: 'Dismiss' },
            description: data.message,
          });
        }
      } catch {
        this.loadingSpinnerStore.hideLocalLoadingSpinner();
        toast.error('Workflow update error', {
          action: { label: 'Dismiss' },
          description: 'An unexpected error occurred',
        });
      }
    },
    async updateJobRefresh(e) {
      try {
        // Update the local state with the new toggle value first
        this.team.useJobRefreshWorkflow = e;

        this.loadingSpinnerStore.showLocalLoadingSpinner();

        // Only proceed with API call if we have a valid team ID
        if (!this.team.id) {
          this.loadingSpinnerStore.hideLocalLoadingSpinner();
          toast.error('Workflow update error', {
            action: { label: 'Dismiss' },
            description: 'Unable to update: Team ID is missing',
          });
          return;
        }

        const response = await fetch('/api/team', {
          method: 'PUT',
          body: JSON.stringify({
            use_job_refresh_workflow: e,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
          },
        });

        const data = await response.json();
        this.loadingSpinnerStore.hideLocalLoadingSpinner();

        if (data.status !== 'error') {
          toast.success('Workflow updated', {
            action: { label: 'Dismiss' },
            description: 'Your workflow was successfully updated. Use the button below to dismiss this alert.',
          });
        } else {
          toast.error('Workflow update error', {
            action: { label: 'Dismiss' },
            description: data.message,
          });
        }
      } catch {
        this.loadingSpinnerStore.hideLocalLoadingSpinner();
        toast.error('Workflow update error', {
          action: { label: 'Dismiss' },
          description: 'An unexpected error occurred',
        });
      }
    },
  },
};
</script>
